import React, { useState, useEffect } from 'react';
import { sets, customFields } from '../utils/api';
import { toast } from 'react-toastify';
import CoinSelector from './shared/CoinSelector';
import TagSelector from './shared/TagSelector';
import FieldSelector from './shared/FieldSelector';
import AdvancedFilterSection from './shared/filters/AdvancedFilterSection';

const FILTER_FIELDS = [
  { id: 'assayer', label: 'Assayer', type: 'text' },
  { id: 'cac_sticker', label: 'CAC Sticker', type: 'select', options: ['green', 'gold', 'none'] },
  { id: 'certification_number', label: 'Certification Number', type: 'text' },
  { id: 'certification_service_id', label: 'Certification Service', type: 'text' },
  { id: 'coin_details', label: 'Coin Details', type: 'text' },
  { id: 'composition', label: 'Composition', type: 'text' },
  { id: 'country', label: 'Country', type: 'text' },
  { id: 'current_pop_higher', label: 'Current Pop Higher', type: 'number' },
  { id: 'current_population', label: 'Current Population', type: 'number' },
  { id: 'denomination', label: 'Denomination', type: 'text' },
  { id: 'diameter', label: 'Diameter', type: 'number' },
  { id: 'die_variety', label: 'Die Variety', type: 'text' },
  { id: 'edge', label: 'Edge', type: 'text' },
  { id: 'grade', label: 'Grade', type: 'text' },
  { id: 'grading_cost', label: 'Grading Cost', type: 'number' },
  { id: 'list_price', label: 'List Price', type: 'number' },
  { id: 'metal', label: 'Metal', type: 'text' },
  { id: 'minor_variety', label: 'Minor Variety', type: 'text' },
  { id: 'mint_mark', label: 'Mint Mark', type: 'text' },
  { id: 'mintage', label: 'Mintage', type: 'text' },
  { id: 'notes', label: 'Notes', type: 'text' },
  { id: 'pcgs_number', label: 'PCGS Number', type: 'text' },
  { id: 'pop_higher_when_bought', label: 'Pop Higher When Bought', type: 'number' },
  { id: 'population_when_bought', label: 'Population When Bought', type: 'number' },
  { id: 'price_guide_value', label: 'Price Guide Value', type: 'number' },
  { id: 'provenance', label: 'Provenance', type: 'text' },
  { id: 'purchase_date', label: 'Purchase Date', type: 'date' },
  { id: 'purchase_price', label: 'Purchase Price', type: 'number' },
  { id: 'reference_number', label: 'Reference Number', type: 'text' },
  { id: 'shipping_cost', label: 'Shipping Cost', type: 'number' },
  { id: 'status', label: 'Status', type: 'text' },
  { id: 'storage_location', label: 'Storage Location', type: 'text' },
  { id: 'total_cost', label: 'Total Cost', type: 'number' },
  { id: 'variety', label: 'Variety', type: 'text' },
  { id: 'weight', label: 'Weight', type: 'number' },
  { id: 'year', label: 'Year', type: 'text' }
];

const CreateSetModal = ({ onClose, onUpdate, navigate }) => {
  const [newSetName, setNewSetName] = useState('');
  const [newSetDescription, setNewSetDescription] = useState('');
  const [setType, setSetType] = useState('static');
  const [selectedCoinIds, setSelectedCoinIds] = useState([]);
  const [allTagIds, setAllTagIds] = useState([]);
  const [anyTagIds, setAnyTagIds] = useState([]);
  const [filters, setFilters] = useState([]);
  const [selectedFields, setSelectedFields] = useState(['year', 'denomination']);
  const [availableCustomFields, setAvailableCustomFields] = useState([]);

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const fields = await customFields.getAll();
        setAvailableCustomFields(fields);
      } catch (err) {
        console.error('Failed to load custom fields:', err);
        toast.error('Failed to load custom fields');
      }
    };

    loadCustomFields();
  }, []);

  const handleCreateSet = async (e) => {
    e.preventDefault();
    if (!newSetName.trim()) return;

    try {
      const data = {
        name: newSetName.trim(),
        description: newSetDescription.trim(),
        type: setType,
        field_preferences: {
          visible_fields: selectedFields
        }
      };

      if (setType === 'static') {
        data.coinIds = selectedCoinIds;
      } else {
        data.filter_criteria = {
          all_tags: allTagIds,
          any_tags: anyTagIds,
          filters: filters
            .filter(filter => 
              // Include filters that:
              // 1. Have a column and operator
              filter.column && filter.operator && (
                // AND either:
                // 1. Have a logicOperator (to preserve the chain)
                filter.logicOperator ||
                // 2. Are isEmpty/isNotEmpty operators (which don't need values)
                ['isEmpty', 'isNotEmpty'].includes(filter.operator) ||
                // 3. Have a value (for the last filter or standalone filters)
                filter.value
              )
            )
            .map((filter, index, arr) => {
              // Only remove logicOperator from the last filter
              if (index === arr.length - 1) {
                const { logicOperator, ...filterWithoutLogic } = filter;
                return filterWithoutLogic;
              }
              return filter;
            })
        };
      }

      const newSet = await sets.create(data);
      setNewSetName('');
      setNewSetDescription('');
      setSelectedCoinIds([]);
      if (onUpdate) onUpdate();
      onClose();
      toast.success('Set created successfully');
      navigate(`/sets/${newSet.id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create set');
    }
  };

  // Combine regular fields and custom fields for the filter section
  const allFields = [
    ...FILTER_FIELDS,
    ...availableCustomFields.map(field => ({
      id: `custom_field_${field.id}`,
      label: field.name,
      type: 'text'
    }))
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex justify-between items-start px-6 py-4 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-xl font-bold text-gray-900">Create New Set</h2>
          <button
            id="close-modal"
            name="close-modal"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleCreateSet} className="space-y-4 p-6">
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Set Type
              </legend>
              <div className="flex gap-4">
              <label htmlFor="set-type-static" className="inline-flex items-center">
                <input
                  type="radio"
                  id="set-type-static"
                  name="set-type"
                  value="static"
                  checked={setType === 'static'}
                  onChange={(e) => setSetType(e.target.value)}
                  className="form-radio h-4 w-4 text-indigo-600"
                />
                <span className="ml-2">Static Set</span>
              </label>
              <label htmlFor="set-type-dynamic" className="inline-flex items-center">
                <input
                  type="radio"
                  id="set-type-dynamic"
                  name="set-type"
                  value="dynamic"
                  checked={setType === 'dynamic'}
                  onChange={(e) => setSetType(e.target.value)}
                  className="form-radio h-4 w-4 text-indigo-600"
                />
                <span className="ml-2">Dynamic Set</span>
              </label>
              </div>
            </fieldset>
          </div>

          <div>
            <label htmlFor="setName" className="block text-sm font-medium text-gray-700">
              Set Name
            </label>
            <input
              id="setName"
              name="setName"
              type="text"
              value={newSetName}
              onChange={(e) => setNewSetName(e.target.value)}
              placeholder="Enter set name"
              className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              maxLength={50}
            />
          </div>
          
          <div>
            <label htmlFor="setDescription" className="block text-sm font-medium text-gray-700">
              Description (optional)
            </label>
            <textarea
              id="setDescription"
              name="setDescription"
              value={newSetDescription}
              onChange={(e) => setNewSetDescription(e.target.value)}
              placeholder="Enter set description"
              className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              rows={2}
            />
          </div>

          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700">
                Visible Fields
              </legend>
              <FieldSelector
                selectedFields={selectedFields}
                onChange={setSelectedFields}
                className="mt-1"
                customFields={availableCustomFields}
              />
            </fieldset>
          </div>
          
          {setType === 'static' ? (
            <div>
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700">
                  Select Coins
                </legend>
                <CoinSelector
                  selectedCoinIds={selectedCoinIds}
                  onChange={setSelectedCoinIds}
                  className="mt-1"
                />
              </fieldset>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700">
                      Must Have ALL These Tags
                    </legend>
                    <div className="mt-2">
                      <TagSelector
                        prefix="all-"
                        selectedTagIds={allTagIds}
                        onChange={setAllTagIds}
                        className="mt-1"
                      />
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mt-4">
                      Must Have ANY of These Tags
                    </legend>
                    <div className="mt-2">
                      <TagSelector
                        prefix="any-"
                        selectedTagIds={anyTagIds}
                        onChange={setAnyTagIds}
                        className="mt-1"
                      />
                    </div>
                  </fieldset>
                </div>

                <div>
                  <label htmlFor="advanced-filters" className="block text-sm font-medium text-gray-700 mb-2">
                    Advanced Filters
                  </label>
                  <AdvancedFilterSection
                    id="advanced-filters"
                    name="advanced-filters"
                    fields={allFields}
                    filters={filters}
                    onChange={setFilters}
                  />
                </div>
              </div>
            </>
          )}

          <button
            type="submit"
            id="create-set"
            name="create-set"
            disabled={!newSetName.trim() || (setType === 'static' && selectedCoinIds.length === 0)}
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
          >
            Create Set
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateSetModal;
